import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useMaintenanceTypeListStore } from "../../../store/masterDataStore";
import { maintenanceTypeListType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm = {
  maintenanceTypeName: "",
  description: "",
  frequency: "",
  typicalDuration: "",
  responsibleDepartment: "",
  priorityLevel: "",
  // status: false,
};

const MaintenanceTypeList: React.FC = () => {
  const navigate = useNavigate();

  const [rowData, setRowData] = useState<maintenanceTypeListType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<maintenanceTypeListType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const {
    maintenanceTypeList,
    fetchAllMaintenanceTypeList,
    createMaintenanceTypeList,
    updateMaintenanceTypeList,
    deleteMaintenanceTypeList,
  } = useMaintenanceTypeListStore();

  useEffect(() => {
    const resData = maintenanceTypeList.map((cat: any) => {
      return {
        ...cat,
      };
    });
    setRowData(resData);
  }, [maintenanceTypeList]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchAllMaintenanceTypeList();
    };
    fetchData();
  }, []);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode && editId) {
        response = await updateMaintenanceTypeList({ ...formData }, editId);
      } else {
        response = await createMaintenanceTypeList(formData);
      }

      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        await fetchAllMaintenanceTypeList();
      }
    } catch (error) {
      console.error("Error creating maintenance type:", error);
    }
  };

  const handleEdit = useCallback(
    (data: maintenanceTypeListType) => {
      setIsEditMode(true);
      setFormData(data);
      setModalOpen(true);
      setEditId(data._id || "");
    },
    [setIsEditMode, setFormData, setEditId]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this maintenance type?"
      );
      if (confirmed) {
        await deleteMaintenanceTypeList(id);
      }
    },
    [deleteMaintenanceTypeList]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "maintenanceTypeName",
        headerName: "Maintenance Type Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
          // whiteSpace: "nowrap",
          // overflow: "hidden",
          // textOverflow: "ellipsis",
        },
        tooltipField: "maintenanceTypeName",
      },
      {
        field: "createdAt",
        headerName: "Created At",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
          //  whiteSpace: "nowrap",
          // overflow: "hidden",
          // textOverflow: "ellipsis",
        },
        valueGetter: (params: any) => {
          return (
            new Date(params.data?.createdAt).toLocaleDateString("en-US") || "NA"
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
        // minWidth: 20,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Maintenance Types"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Maintenance Types"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    [navigate]
  );

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">MaintenanceType List</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Maintenance Type
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  // loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">No data added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-4">
            {isEditMode
              ? "Update Maintenance Type"
              : "Create New Maintenance Type"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="maintenanceTypeForm"
            className="flex flex-col gap-6 pb-2"
          >
            <div className="flex flex-col gap-2">
              <label
                htmlFor="maintenanceTypeName"
                className="text-sm font-medium text-gray-700"
              >
                Maintenance Type Name <span className="text-red-500">*</span>
              </label>
              <input
                id="maintenanceTypeName"
                name="maintenanceTypeName"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.maintenanceTypeName}
                onChange={handleChange}
                placeholder="Enter maintenance type name"
                required
                aria-required="true"
                aria-label="Maintenance Type Name"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description (optional)"
                rows={3}
                aria-label="Description"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="frequency"
                className="text-sm font-medium text-gray-700"
              >
                Frequency
              </label>
              <select
                id="frequency"
                name="frequency"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.frequency || ""}
                onChange={handleChangeSelect}
                aria-label="Frequency"
              >
                <option value="">Select Frequency</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="typicalDuration"
                className="text-sm font-medium text-gray-700"
              >
                Typical Duration
              </label>
              <input
                id="typicalDuration"
                name="typicalDuration"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.typicalDuration}
                onChange={handleChange}
                placeholder="E.g., 2 hours"
                aria-label="Typical Duration"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="responsibleDepartment"
                className="text-sm font-medium text-gray-700"
              >
                Responsible Department
              </label>
              <input
                id="responsibleDepartment"
                name="responsibleDepartment"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.responsibleDepartment}
                onChange={handleChange}
                placeholder="Department responsible for maintenance"
                aria-label="Responsible Department"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="priorityLevel"
                className="text-sm font-medium text-gray-700"
              >
                Priority Level
              </label>
              <select
                id="priorityLevel"
                name="priorityLevel"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.priorityLevel || ""}
                onChange={handleChangeSelect}
                aria-label="Priority Level"
              >
                <option value="">Select Priority</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            {/* <div className="flex items-center gap-2">
              <input
                id="status"
                name="status"
                type="checkbox"
                checked={formData.status || false}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.checked })
                }
                className="w-4 h-4 text-[#00A8A8] border-gray-300 rounded focus:ring-[#00A8A8]"
                aria-checked={formData.status || false}
                aria-label="Active Status"
              />
              <label
                htmlFor="status"
                className="text-sm font-medium text-gray-700 select-none cursor-pointer mt-2"
              >
                Active
              </label>
            </div> */}
          </form>
        </div>
        <div className="h-16" />

        <div className="flex justify-end gap-3 p-2 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="maintenanceTypeForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default MaintenanceTypeList;
